// Slice 7: Weekly Update Copilot.
import { useEffect, useState } from "react";
import { FileEdit, Sparkles, Send } from "lucide-react";

const BASE = import.meta.env.BASE_URL;
const auth = (): Record<string, string> => ({ "x-founder-token": localStorage.getItem("founder_token") || "" });

interface Question { key: string; prompt: string; }
interface Draft {
  id: number; weekOf: string; questions: Question[]; answers: Record<string, string>;
  draftBody: string | null; status: string;
  founderApprovedAt: string | null; advisorApprovedAt: string | null; publishedAt: string | null;
}

export default function FounderWeeklyUpdate() {
  const [draft, setDraft] = useState<Draft | null>(null);
  const [previous, setPrevious] = useState<{ weekOf: string; status: string; draftBody: string | null } | null>(null);
  const [companyName, setCompanyName] = useState("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [body, setBody] = useState("");

  async function load() {
    const r = await fetch(`${BASE}api/founder-portal/weekly-update`, { headers: auth() });
    if (!r.ok) { setErr("Could not load."); setLoading(false); return; }
    const d = await r.json();
    setDraft(d.draft); setPrevious(d.previous); setCompanyName(d.companyName);
    setBody(d.draft.draftBody || "");
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  async function saveAnswers(answers: Record<string, string>) {
    if (!draft) return;
    setDraft({ ...draft, answers });
    await fetch(`${BASE}api/founder-portal/weekly-update/answers`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...auth() },
      body: JSON.stringify({ id: draft.id, answers }),
    });
  }
  async function stitch() {
    if (!draft || busy) return;
    setBusy(true); setErr(null);
    try {
      const r = await fetch(`${BASE}api/founder-portal/weekly-update/stitch`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...auth() },
        body: JSON.stringify({ id: draft.id }),
      });
      if (!r.ok) { setErr("Sia could not draft."); return; }
      const d = await r.json();
      setDraft({ ...draft, draftBody: d.draftBody, status: "draft_ready" });
      setBody(d.draftBody);
    } finally { setBusy(false); }
  }
  async function saveBody() {
    if (!draft || busy) return;
    setBusy(true);
    try {
      await fetch(`${BASE}api/founder-portal/weekly-update/edit-body`, {
        method: "POST", headers: { "Content-Type": "application/json", ...auth() },
        body: JSON.stringify({ id: draft.id, draftBody: body }),
      });
    } finally { setBusy(false); }
  }
  async function submit() {
    if (!draft || busy) return;
    setBusy(true);
    try {
      await saveBody();
      const r = await fetch(`${BASE}api/founder-portal/weekly-update/submit`, {
        method: "POST", headers: { "Content-Type": "application/json", ...auth() },
        body: JSON.stringify({ id: draft.id }),
      });
      if (r.ok) { setDraft({ ...draft, status: "founder_approved", founderApprovedAt: new Date().toISOString() }); }
    } finally { setBusy(false); }
  }

  if (loading) return <div className="p-12 text-white/60 text-sm">Loading.</div>;
  if (!draft) return <div className="p-12 text-red-400 text-sm">{err}</div>;

  const allAnswered = draft.questions.every(q => (draft.answers[q.key] || "").trim().length > 0);
  const sent = ["founder_approved", "advisor_approved", "published"].includes(draft.status);

  return (
    <div className="max-w-3xl mx-auto px-6 py-8">
      <div className="mb-6">
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-white/60 mb-2">Weekly Update</div>
        <h1 className="text-2xl font-bold tracking-tight mb-2">Week of {draft.weekOf}</h1>
        <p className="text-sm text-white/65 leading-relaxed">
          Answer five short questions. Sia stitches the draft. You edit. Then it goes to your Advisor for review.
        </p>
      </div>

      {/* Status bar */}
      <div className="mb-6 flex items-center gap-2 text-xs">
        {[
          { k: "questions_pending", label: "Answer" },
          { k: "draft_ready", label: "Draft" },
          { k: "founder_approved", label: "Sent to Advisor" },
          { k: "advisor_approved", label: "Approved" },
          { k: "published", label: "Published" },
        ].map((s, i, arr) => {
          const idx = arr.findIndex(x => x.k === draft.status);
          const done = i <= idx;
          return (
            <div key={s.k} className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${done ? "bg-[#FF5C00]" : "bg-white/15"}`} />
              <span className={done ? "text-white/85 font-medium" : "text-white/60"}>{s.label}</span>
              {i < arr.length - 1 && <span className="text-white/60">·</span>}
            </div>
          );
        })}
      </div>

      {/* Questions */}
      {!sent && (
        <div className="space-y-3 mb-6">
          {draft.questions.map(q => (
            <div key={q.key} className="bg-white/[0.04] border border-white/[0.08] rounded-lg p-4">
              <label className="text-xs font-medium text-white/75 block mb-2 leading-relaxed">{q.prompt}</label>
              <textarea
                value={draft.answers[q.key] || ""}
                onChange={e => saveAnswers({ ...draft.answers, [q.key]: e.target.value })}
                rows={2} maxLength={2000}
                data-testid={`input-answer-${q.key}`}
                className="w-full bg-black/40 border border-white/[0.12] rounded px-3 py-2 text-sm text-white outline-none focus:border-[#FF5C00]/50 resize-none"
              />
            </div>
          ))}
        </div>
      )}

      {/* Stitch button or draft */}
      {!draft.draftBody && !sent && (
        <button
          onClick={stitch} disabled={busy || !allAnswered}
          data-testid="button-stitch-draft"
          className="inline-flex items-center gap-2 bg-[#FF5C00] hover:bg-[#FF5C00]/90 disabled:opacity-40 text-white text-sm font-semibold rounded-md px-4 py-2"
        ><Sparkles className="w-3.5 h-3.5" /> {busy ? "Drafting" : allAnswered ? "Ask Sia to draft" : "Answer all questions first"}</button>
      )}

      {(draft.draftBody || sent) && (
        <div className="bg-white/[0.04] border border-white/[0.08] rounded-xl p-5 mb-4">
          <div className="text-xs font-mono uppercase tracking-wider text-white/60 mb-3">Draft</div>
          {sent ? (
            <pre className="text-sm text-white/85 whitespace-pre-wrap font-sans leading-relaxed">{draft.draftBody}</pre>
          ) : (
            <textarea
              value={body} onChange={e => setBody(e.target.value)} onBlur={saveBody}
              rows={12} maxLength={10000}
              data-testid="input-draft-body"
              className="w-full bg-black/40 border border-white/[0.12] rounded px-3 py-2 text-sm text-white outline-none focus:border-[#FF5C00]/50 leading-relaxed font-sans resize-y"
            />
          )}
        </div>
      )}

      {draft.draftBody && !sent && (
        <button
          onClick={submit} disabled={busy}
          data-testid="button-submit-update"
          className="inline-flex items-center gap-2 bg-[#FF5C00] hover:bg-[#FF5C00]/90 disabled:opacity-40 text-white text-sm font-semibold rounded-md px-4 py-2"
        ><Send className="w-3.5 h-3.5" /> {busy ? "Sending" : "Send to my Advisor"}</button>
      )}

      {sent && (
        <div className="bg-green-500/5 border border-green-500/20 rounded-md p-3 text-sm text-white/80">
          Sent to your Advisor. They will review and either approve or send back changes.
        </div>
      )}

      {previous && (
        <div className="mt-10 pt-6 border-t border-white/[0.08]">
          <div className="text-xs font-mono uppercase tracking-wider text-white/60 mb-3">Last week ({previous.weekOf}) · {previous.status}</div>
          {previous.draftBody && <pre className="text-xs text-white/60 whitespace-pre-wrap font-sans leading-relaxed">{previous.draftBody}</pre>}
        </div>
      )}
    </div>
  );
}
